import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { apiGet, setApiFocusHeaders } from "../lib/api";
import type { Dealer, Group } from "../lib/types";
import { useAuth } from "../auth/AuthContext";
import {
  shouldDropStoredDealerFocus,
  type FocusLoadState,
} from "./focus_rules";

interface ScopeValue {
  dealers: Dealer[];
  groups: Group[];
  loading: boolean;
  focusedGroupId: string | null;
  focusedDealerId: string | null;
  setFocusedGroupId: (id: string | null) => void;
  setFocusedDealerId: (id: string | null) => void;
  /** All dealers in the user's role-based scope, narrowed by focusedGroupId. */
  dealersInFocus: Dealer[];
}

const Ctx = createContext<ScopeValue | null>(null);
const GROUP_KEY = "savvy.rep.focusedGroupId";
const DEALER_KEY = "savvy.rep.focusedDealerId";

function safeStorageGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeStorageSet(key: string, val: string | null) {
  try {
    if (val === null) localStorage.removeItem(key);
    else localStorage.setItem(key, val);
  } catch {
    /* private mode / quota */
  }
}

export function ScopeProvider({ children }: { children: ReactNode }) {
  // `authLoading` matters as much as `user` here. This provider is mounted
  // OUTSIDE RequireAuth, so on every page load it renders once with `user`
  // still null while /api/auth/me is in flight. Treating that tick as "signed
  // out, no dealers" is what used to delete the stored store focus.
  const { user, loading: authLoading } = useAuth();
  const [dealers, setDealers] = useState<Dealer[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  // How far the dealer list actually got — distinct from `loading`, because
  // "empty" and "never arrived" must not look the same to the stale-focus
  // guard. See `shouldDropStoredDealerFocus`.
  const [loadState, setLoadState] = useState<FocusLoadState>("auth-pending");
  const [focusedGroupId, setFocusedGroupIdState] = useState<string | null>(() =>
    safeStorageGet(GROUP_KEY),
  );
  const [focusedDealerId, setFocusedDealerIdState] = useState<string | null>(
    () => safeStorageGet(DEALER_KEY),
  );

  // Read by setFocusedDealerId, which must not re-create itself when the group
  // changes (it is passed to memoised children).
  const focusedGroupIdRef = useRef(focusedGroupId);
  focusedGroupIdRef.current = focusedGroupId;

  // Push focus into the api module so every request carries the headers.
  //
  // This effect covers the initial mount and the storage-restore path. It is NOT
  // enough on its own, and the setters below write the headers synchronously as
  // well — see the note there. Keeping both is deliberate: the effect is the
  // catch-all, the synchronous write is what makes a store switch correct.
  useEffect(() => {
    setApiFocusHeaders({
      focusGroupId: focusedGroupId,
      focusDealerId: focusedDealerId,
    });
  }, [focusedGroupId, focusedDealerId]);

  useEffect(() => {
    // Auth hasn't answered yet: no dealers to fetch, and nothing to conclude.
    // Staying `loading` here is deliberate — it keeps the stale-focus guard
    // from running against a list that was never requested.
    if (authLoading) {
      setLoadState("auth-pending");
      setLoading(true);
      return;
    }
    if (!user) {
      setDealers([]);
      setGroups([]);
      setLoadState("auth-pending");
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setLoadState("loading");
    Promise.all([
      apiGet<{ dealers: Dealer[] }>("/api/dealers"),
      apiGet<{ groups: Group[] }>("/api/groups"),
    ])
      .then(([d, g]) => {
        if (cancelled) return;
        setDealers(d.dealers);
        setGroups(g.groups);

        // Drop stored focus if it's no longer valid.
        if (
          focusedGroupId &&
          !g.groups.some((row) => row.id === focusedGroupId)
        ) {
          setFocusedGroupIdState(null);
          safeStorageSet(GROUP_KEY, null);
        }
        if (
          focusedDealerId &&
          !d.dealers.some((row) => row.id === focusedDealerId)
        ) {
          setFocusedDealerIdState(null);
          safeStorageSet(DEALER_KEY, null);
        }
        if (!cancelled) setLoadState("loaded");
      })
      .catch((err: unknown) => {
        console.error("scope load failed:", err);
        // A failed fetch leaves `dealers` empty. Saying so explicitly stops the
        // guard below reading that emptiness as "your store is gone".
        if (!cancelled) setLoadState("failed");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading]);

  const setFocusedGroupId = useCallback(
    (id: string | null) => {
      setFocusedGroupIdState(id);
      safeStorageSet(GROUP_KEY, id);
      // Same reason as setFocusedDealerId below — headers must be current before
      // any child effect fires its request.
      setApiFocusHeaders({ focusGroupId: id, focusDealerId: null });
      // If the new group doesn't contain the currently-focused dealer, clear it.
      if (id && focusedDealerId) {
        const stillVisible = dealers.some(
          (d) => d.id === focusedDealerId && d.groupId === id,
        );
        if (!stillVisible) {
          setFocusedDealerIdState(null);
          safeStorageSet(DEALER_KEY, null);
        }
      }
      if (id === null) {
        // Clearing the group also clears any stored dealer narrowing — picker
        // returns to "all visible stores in role scope".
        setFocusedDealerIdState(null);
        safeStorageSet(DEALER_KEY, null);
      }
    },
    [dealers, focusedDealerId],
  );

  const setFocusedDealerId = useCallback((id: string | null) => {
    setFocusedDealerIdState(id);
    safeStorageSet(DEALER_KEY, id);
    // Synchronously, NOT in the effect above. React runs child effects before the
    // provider's, so on a store switch OutreachStatusStrip re-fetched with
    // ?dealerId=<new store> while the x-focused-dealer-id header still said the
    // OLD one. resolveDealerIdsInScope intersects the two, got an empty set, and
    // the strip reported "No campaign is set up for this rooftop yet" for a
    // rooftop with two campaigns. Deterministic on every switch, not a race.
    setApiFocusHeaders({ focusGroupId: focusedGroupIdRef.current, focusDealerId: id });
  }, []);

  const dealersInFocus = useMemo(() => {
    if (!focusedGroupId) return dealers;
    return dealers.filter((d) => d.groupId === focusedGroupId);
  }, [dealers, focusedGroupId]);

  // Stale-storage guard: a stored dealer focus from ANOTHER group passes the
  // exists-at-all check above but empties every scoped query (group ∩ dealer = ∅),
  // while the dealer <select> silently displays its placeholder. Drop it.
  //
  // The decision itself lives in `shouldDropStoredDealerFocus`, which refuses
  // to act on a list it hasn't actually received. Gating on `loading` alone was
  // not enough: the provider sits outside RequireAuth, so the first render set
  // `loading: false` with `dealers: []` while auth was still in flight, and
  // this guard deleted the stored store on every single page load.
  useEffect(() => {
    if (
      shouldDropStoredDealerFocus({
        state: loadState,
        storedDealerId: focusedDealerId,
        dealersInFocus: dealersInFocus.map((d) => d.id),
      })
    ) {
      setFocusedDealerId(null);
    }
  }, [loadState, focusedDealerId, dealersInFocus, setFocusedDealerId]);

  const value = useMemo<ScopeValue>(
    () => ({
      dealers,
      groups,
      loading,
      focusedGroupId,
      focusedDealerId,
      setFocusedGroupId,
      setFocusedDealerId,
      dealersInFocus,
    }),
    [
      dealers,
      groups,
      loading,
      focusedGroupId,
      focusedDealerId,
      setFocusedGroupId,
      setFocusedDealerId,
      dealersInFocus,
    ],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useScope(): ScopeValue {
  const v = useContext(Ctx);
  if (!v) throw new Error("useScope must be used inside <ScopeProvider>");
  return v;
}
